import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { useRect, useWindowOn } from 'react-use-rect'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useScroll } from '#app/hooks/use-scroll.ts'
import { clamp, mapRange } from '#app/utils/maths.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'

export function EnvironmentSocialGovernance() {
	const { t } = useTranslation('home')
	const location = useLocation()
	const [top, setTop] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [progress, setProgress] = useState({
		first: 0,
		second: 0,
		third: 0,
		fourth: 0,
		fifth: 0,
		all: 0,
		forArrows: 0,
	})
	const [rectRef, revalidate] = useRect((rect) => {
		setTop(rect.top)
		setHeight(rect.height)
	})
	useWindowOn('scroll', () => revalidate())

	useScroll(({ scroll }) => {
		if (!top || !height) return

		const start = top + scroll
		// const end = top + scroll + height - windowHeight

		const getPercentage = (percentage: number) => {
			if (percentage < 0 || percentage > 1)
				throw new Error('Invalid percentage')
			return start + height * percentage
		}

		// mapRange(start, end, scroll, 0, 1)
		const p1 = clamp(
			0,
			mapRange(getPercentage(0.05), getPercentage(0.15), scroll, 0, 1),
			1,
		)
		const p2 = clamp(
			0,
			mapRange(getPercentage(0.18), getPercentage(0.26), scroll, 0, 1),
			1,
		)
		const p3 = clamp(
			0,
			mapRange(getPercentage(0.26), getPercentage(0.37), scroll, 0, 1),
			1,
		)
		const p4 = clamp(
			0,
			mapRange(getPercentage(0.37), getPercentage(0.48), scroll, 0, 1),
			1,
		)
		const p5 = clamp(
			0,
			mapRange(getPercentage(0.48), getPercentage(0.6), scroll, 0, 1),
			1,
		)
		const p1to5 = clamp(
			0,
			mapRange(getPercentage(0.05), getPercentage(0.6), scroll, 0, 1),
			1,
		)
		const forArrows = clamp(
			0,
			mapRange(getPercentage(0.6), getPercentage(0.85), scroll, 0, 1),
			1,
		)

		setProgress({
			first: p1,
			second: p2,
			third: p3,
			fourth: p4,
			fifth: p5,
			all: p1to5,
			forArrows,
		})
	})

	return (
		// Wrapper
		<LandingSection borderToBorderOnX>
			<div
				ref={rectRef}
				className="relative h-[250vh] overflow-visible sm:h-[320vh]"
			>
				{/* Inner */}
				<div className="sticky top-0">
					<div className="relative flex h-screen flex-col items-center justify-center">
						<img
							style={{
								// Duplicate the progress of the image to make it fade in faster
								opacity: progress.first * 0.4,
							}}
							className={cn(
								'absolute inset-0 z-[-1] h-full w-full object-cover brightness-75 lg:brightness-100',
							)}
							src="/img/homepage/leaf-background.webp"
							alt="Leaf background"
						/>
						<div
							style={{
								opacity: 1 - progress.second,
							}}
							className={cn(
								'z-10 m-0 mx-auto flex h-full w-full items-center justify-center self-center p-sm lg:p-0',
							)}
						>
							<div className="flex flex-col items-end gap-xl lg:flex-row">
								<MarketingImage
									className="xl:max-w-md 2xl:max-w-3xl"
									src="/img/homepage/hand-feeling-leafs.webp"
									alt="Enviromental Social Governance"
								/>
								<div className="flex flex-col gap-sm md:max-w-md">
									<h2 className="font-serif text-h2 !font-extralight uppercase leading-[2.5rem] md:text-h1 md:leading-[3rem]">
										{t('environment.title')}
									</h2>
									<p className="text-body-2xs font-extralight xl:text-body-xs">
										{t('environment.description')}
									</p>
								</div>
							</div>
						</div>
						<div
							className="container absolute inset-0 top-1/2 z-20 grid max-w-[100vw] -translate-y-[40vh] grid-rows-[repeat(3,1fr)] items-center gap-y-2 text-brand-dusk xl:-translate-y-[35vh] xl:gap-0"
							style={{
								opacity: progress.third,
							}}
						>
							<div className={'sr-only'}>
								Green certifications and internationally recognized practices
								promoting environmental sustainability are fundamental pillars
								of our business philosophy. These standards not only signify our
								commitment to environmental protection but also embody our
								purpose of building mutual respect towards the environment and
								the communities where we operate. This holistic approach allows
								us to build relationships based on trust and understanding.
								Ensuring that our activities are not only sustainable in the
								present but also contribute to a more prosperous and balanced
								future for all, achieving a new level of well-being improving
								the quality of life for future generations.
							</div>
							{/* First, show with progress.third */}
							<div
								className={
									'flex flex-col gap-2 self-center xl:items-center xl:gap-0'
								}
							>
								<div
									className={
										'flex flex-col gap-2 xl:translate-x-[12%] xl:flex-row xl:items-center xl:gap-6'
									}
								>
									{/*text-[clamp(3vh,7.5vw,10rem)]*/}
									<Text
										as={'p'}
										type={'serif'}
										weight={'thin'}
										className={
											'text-[clamp(2.5rem,calc(5vw+1rem),9rem)] uppercase leading-none'
										}
									>
										GREEN
									</Text>
									<p
										className={
											'max-w-[650px] text-[clamp(1.5vh,2vw,1.025rem)] font-light uppercase leading-tight tracking-widest xl:max-w-[400px] xl:font-extralight'
										}
									>
										Certifications and internationally recognized practices
										promoting environmental sustainability are fundamental
										pillars of out business philosophy.
									</p>
								</div>
								<div
									className={
										'flex flex-col gap-2 xl:-translate-x-[17%] xl:flex-row xl:items-start xl:gap-x-6'
									}
								>
									<p
										className={
											'max-w-[650px] text-[clamp(1.5vh,2vw,1.025rem)] font-light uppercase leading-tight tracking-widest xl:mt-4 xl:max-w-[350px] xl:text-right xl:font-extralight'
										}
									>
										These standards not only signify our commitment to
										environmental protection but also embody out purpose of
									</p>
									<Text
										as={'p'}
										type={'serif'}
										weight={'thin'}
										className={
											'text-[clamp(2.5rem,calc(5vw+1rem),9rem)] uppercase leading-none'
										}
									>
										BUILDING
									</Text>
								</div>
							</div>

							{/* Second, show with progress.fourth */}
							<div
								style={{
									opacity: progress.fourth,
								}}
								className={
									'flex flex-col flex-wrap gap-2 xl:flex-row xl:items-center xl:justify-center xl:gap-x-6 xl:gap-y-0'
								}
							>
								<Text
									as={'p'}
									type={'serif'}
									weight={'thin'}
									className={
										'text-[clamp(2.5rem,calc(5vw+1rem),9rem)] uppercase leading-none'
									}
								>
									MUTUAL RESPECT
								</Text>
								<p
									className={
										'max-w-[650px] text-[clamp(1.5vh,2vw,1.025rem)] font-light uppercase leading-tight tracking-widest xl:max-w-[400px] xl:font-extralight'
									}
								>
									Towards the environment and the communities where we operate.
									This holistic approach allows us to build relationships based
									on trust and understanding.
								</p>
							</div>

							{/* Third, show with progress fifth */}
							<div
								style={{
									opacity: progress.fifth,
								}}
								className={
									'mt-2 flex flex-col gap-2 xl:mt-0 xl:items-center xl:gap-0'
								}
							>
								<div
									className={
										'flex flex-col gap-2 xl:-translate-x-[4%] xl:flex-row xl:items-center xl:gap-6'
									}
								>
									<p
										className={
											'max-w-[650px] text-[clamp(1.5vh,2vw,1.025rem)] font-light uppercase leading-tight tracking-widest xl:max-w-[400px] xl:text-end xl:font-extralight'
										}
									>
										Ensuring that our activities are not only sustainable in the
										present but also contribute to a more prosperous and
										balanced future for all, achieving
									</p>
									<Text
										as={'p'}
										type={'serif'}
										weight={'thin'}
										className={
											'text-[clamp(2.5rem,calc(5vw+1rem),9rem)] uppercase leading-none'
										}
									>
										A NEW LEVEL
									</Text>
								</div>
								<div
									className={
										'flex flex-col gap-2 xl:translate-x-[30%] xl:items-end xl:gap-0'
									}
								>
									<Text
										as={'p'}
										type={'serif'}
										weight={'thin'}
										className={
											'text-[clamp(2.5rem,calc(5vw+1rem),9rem)] uppercase leading-none'
										}
									>
										OF WELL-BEING
									</Text>
									<p
										className={
											'text-[clamp(1.5vh,2vw,1.025rem)] font-light uppercase leading-tight tracking-widest xl:text-end xl:font-extralight'
										}
									>
										Improving the quality of life for future generations.
									</p>
								</div>
							</div>
						</div>

						<div className="absolute bottom-xxl">
							<div className="relative h-px w-[50vw] bg-brand-dusk md:w-[25vw]">
								<div
									className="absolute left-0 top-0 h-[3px] -translate-y-1/2 bg-brand-dusk"
									style={{
										width: `${progress.all * 100}%`,
									}}
								/>
								{!location.pathname.endsWith('sustainability') && (
									<>
										<div
											className="absolute right-0 top-0 h-[3px] max-w-[20px] origin-bottom-right -translate-y-1/2 rotate-[45deg] bg-brand-dusk"
											style={{
												width: `${progress.forArrows * 100}%`,
											}}
										/>
										<div
											className="absolute right-0 top-0 h-[3px] max-w-[20px] origin-top-right -translate-y-1/2 rotate-[-45deg] bg-brand-dusk"
											style={{
												width: `${progress.forArrows * 100}%`,
											}}
										/>
									</>
								)}
							</div>
						</div>

						{!location.pathname.endsWith('sustainability') && (
							<Link
								viewTransition
								className="absolute inset-0 z-30"
								to={routes.public.landings.sustainability.route({})}
							></Link>
						)}
					</div>
				</div>
			</div>
		</LandingSection>
	)
}
